import Cookies from 'js-cookie'
import { trackEvent } from '@fs/zion-analytics'
import { baseUrl } from '@fs/zion-config'
import axios from '../axios'
import { FieldNames, Urls } from '../constants'
import {
  MultiValidationError,
  AlreadyLinkedError,
  DisabledAccountError,
  MaybeIncorrectUsernameError,
  MaybeIncorrectPasswordError,
  UnverifiedEmailError,
  UnverifiedSmsError,
  LockedAccountError,
  NeedsParentalConsentEmailError,
  NeedsParentalConsentSmsError,
} from '../errors'
import logError from './logError'
import redirectAsync from '../utils/redirectAsync'

export default async function login(
  { [FieldNames.USERNAME]: username, [FieldNames.CURRENT_PASSWORD]: password },
  action
) {
  const urlencoded = new URLSearchParams()
  urlencoded.append('username', username)
  urlencoded.append('password', password)
  urlencoded.append('_csrf', Cookies.get('XSRF-TOKEN'))
  if (action) {
    urlencoded.append('action', action)
  }

  const response = await axios.post(`${window.location.origin}/login`, urlencoded, {
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    xsrfCookieName: 'made-up-cookie-so-we-do-not-sent',
  })
  const { loginError, redirectUrl } = response.data || {}
  if (!loginError && !redirectUrl) {
    logError('No redirectUrl was returned in Authn login call.')
    return redirectAsync(baseUrl)
  }
  if (redirectUrl) {
    await trackIfLinking()
    return redirectAsync(redirectUrl)
  }
  switch (loginError) {
    case 'loginOutsideOauth':
      return redirectAsync(Urls.SESSION_EXPIRED)
    case 'BadCredentialsException':
      throw new MultiValidationError([new MaybeIncorrectUsernameError(), new MaybeIncorrectPasswordError()])
    case 'NotActivatedParentalEmailException':
      throw new MultiValidationError([new NeedsParentalConsentEmailError(username)])
    case 'NotActivatedParentalSMSException':
      throw new MultiValidationError([new NeedsParentalConsentSmsError(username)])
    case 'NotActivatedException':
    case 'NotActivatedEmailException':
      throw new MultiValidationError([new UnverifiedEmailError(username)])
    case 'NotActivatedSMSException':
      throw new MultiValidationError([new UnverifiedSmsError(username)])
    case 'LockedException':
      throw new MultiValidationError([new LockedAccountError()])
    case 'RateLimitException':
      throw new MultiValidationError([new LockedAccountError()])
    case 'DisabledException':
      throw new MultiValidationError([new DisabledAccountError()])
    case 'AlreadyLinkedException':
      throw new MultiValidationError([new AlreadyLinkedError()])
    default: {
      const message = `Unknown login error returned ${loginError}`
      throw new Error(message)
    }
  }
}

// https://docs.google.com/spreadsheets/d/1VsfZTzsI4nQre8qt5WbKVBK9M7EwEExvmMcf_-j8bSQ
async function trackIfLinking() {
  const pathname = window.location.pathname
  if (!pathname.includes(Urls.LINK)) {
    return
  }
  const event = {
    event_name: 'login_link',
    registration_provider: new URLSearchParams(window.location.search).get('provider'),
    // Trying to reuse some existing analytics properties that could work here.
    registration_duplicateField: pathname.includes(Urls.LINK_EXISTING_ACCOUNT) ? 'unknown' : undefined,
  }
  trackEvent(event)
  // Allow a few ms for analytics tracking to go out
  // 500ms is recommended by Kevin Willeitner
  await new Promise((resolve) => {
    setTimeout(resolve, 500)
  })
}
